import { create } from 'zustand';
import { getChannel, joinUserChannel } from '../lib/realtime';
import { v4 as uuidv4 } from 'uuid';
import { notificationAPI } from '../lib/api';
import { useToastStore } from './toastStore';
import { useMessageStore } from './messageStore';
import { useServerStore } from './serverStore';

export interface Notification {
  id: string;
  type: 'friend_request' | 'message' | 'mention' | 'server_invite' | 'system';
  content?: string;
  read: boolean;
  created_at: string;
  from?: {
    id: string;
    username: string;
    avatar_url: string | null;
  };
  server?: {
    id: string;
    name: string;
  };
  channel?: {
    id: string;
    name: string;
  };
  message_id?: string;
}

function normalizeNotification(raw: any): Notification {
  return {
    id: raw.id,
    type: raw.type,
    content: raw.content,
    read: Boolean(raw.read ?? raw.is_read),
    created_at: raw.created_at,
    from: raw.from
      ? {
          id: raw.from.id,
          username: raw.from.username,
          avatar_url: raw.from.avatar_url ?? null,
        }
      : raw.sender_id
      ? {
          id: raw.sender_id,
          username: raw.sender_username || 'Un utilisateur',
          avatar_url: raw.sender_avatar_url ?? null,
        }
      : undefined,
    server: raw.server
      ? { id: raw.server.id, name: raw.server.name }
      : raw.server_id
      ? { id: raw.server_id, name: raw.server_name || '' }
      : undefined,
    channel: raw.channel
      ? { id: raw.channel.id, name: raw.channel.name }
      : raw.channel_id
      ? { id: raw.channel_id, name: raw.channel_name || '' }
      : undefined,
    message_id: raw.message_id,
  };
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  removeNotification: (id: string) => Promise<void>;
  clearNotifications: () => Promise<void>;
  subscribeToNotifications: () => () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  
  fetchNotifications: async () => {
    try {
      set({ isLoading: true, error: null });
      const raw = await notificationAPI.getNotifications();
      const notifications = (raw || []).map(normalizeNotification);
      set({ notifications });
      return notifications;
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch notifications' });
      console.error('Error fetching notifications:', error);
    } finally {
      set({ isLoading: false });
    }
  },
  
  fetchUnreadCount: async () => {
    try {
      const count = await notificationAPI.getUnreadCount();
      set({ unreadCount: count });
      return count;
    } catch (error: any) {
      console.error('Error fetching unread count:', error);
    }
  },
  
  markAsRead: async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      set(state => ({
        notifications: state.notifications.map(n => 
          n.id === id ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      }));
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
    }
  },
  
  markAllAsRead: async () => {
    try {
      await notificationAPI.markAllAsRead();
      set(state => ({
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0
      }));
    } catch (error: any) {
      console.error('Error marking all notifications as read:', error);
    }
  },
  
  removeNotification: async (id) => {
    try {
      await notificationAPI.deleteNotification(id);
      set(state => {
        const notification = state.notifications.find(n => n.id === id);
        return {
          notifications: state.notifications.filter(n => n.id !== id),
          unreadCount: notification && !notification.read 
            ? state.unreadCount - 1 
            : state.unreadCount
        };
      });
    } catch (error: any) {
      console.error('Error removing notification:', error);
    }
  },
  
  clearNotifications: async () => {
    try {
      await notificationAPI.deleteAllNotifications();
      set({ notifications: [], unreadCount: 0 });
    } catch (error: any) {
      console.error('Error clearing notifications:', error);
    }
  },
  
  subscribeToNotifications: () => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return () => {};
    
    // Ensure user channel is joined before subscribing
    joinUserChannel(userId);
    const channel = getChannel(`user:${userId}`);
    if (!channel) return () => {};
    
    const notifRef = channel.on('notification', (incoming: any) => {
      const notification = normalizeNotification(incoming);
      set(state => ({
        notifications: [notification, ...state.notifications],
        unreadCount: state.unreadCount + 1
      }));

      // Check if the source of this notification is currently active
      // If so, skip the visual/OS notification
      const messageState = useMessageStore.getState();
      const serverState = useServerStore.getState();

      // Skip DM notification if that DM is currently open
      if (notification.type === 'message' && !notification.channel && notification.from?.id) {
        // Track unread DM count in messageStore
        messageState.incrementUnreadDm(
          notification.from.id,
          notification.from.username || 'User',
          notification.from.avatar_url || null,
        );
        // Update conversation list with latest message preview
        messageState.updateConversation(
          notification.from.id,
          notification.content || '',
          notification.from.id,
        );
        if (messageState.currentDmUser === notification.from.id) return;
      }

      // Skip channel notification if that channel is currently active
      if (notification.channel?.id) {
        if (serverState.currentChannel?.id === notification.channel.id) return;
      }

      // Build toast content
      let title = 'Erozion';
      let body = notification.content || '';
      if (notification.type === 'friend_request') {
        title = 'Friend Request';
        body = notification.content || `${notification.from?.username || 'Someone'} sent you a friend request`;
      } else if (notification.type === 'mention') {
        title = notification.server?.name
          ? `#${notification.channel?.name || 'channel'} · ${notification.server.name}`
          : `${notification.from?.username || 'Someone'}`;
        body = notification.content ? notification.content.substring(0, 120) : 'mentioned you';
      } else if (notification.type === 'message') {
        if (notification.server?.name) {
          title = `#${notification.channel?.name || 'channel'} · ${notification.server.name}`;
        } else {
          title = notification.from?.username || 'Direct Message';
        }
        body = notification.content ? notification.content.substring(0, 120) : '';
      } else if (notification.type === 'system') {
        title = 'Erozion';
      }

      const isAppFocused = document.hasFocus();

      if (isAppFocused) {
        // In-app toast
        useToastStore.getState().addToast({
          type: notification.type === 'friend_request' ? 'friend_request'
            : notification.type === 'mention' ? 'mention'
            : notification.channel ? 'message'
            : 'dm',
          title,
          body,
          avatarUrl: notification.from?.avatar_url,
          duration: 5000,
          action: notification.from?.id && !notification.channel
            ? { type: 'dm', userId: notification.from.id }
            : notification.channel?.id
            ? { type: 'channel', channelId: notification.channel.id, serverId: notification.server?.id }
            : undefined,
        });
      }

      // Send to Electron main process (passes focus so main can skip if toast shown)
      if (typeof window !== 'undefined' && (window as any).api?.showNotification) {
        (window as any).api.showNotification({ title, body, appFocused: isAppFocused });
      } else if (!isAppFocused && typeof window !== 'undefined' && 'Notification' in window) {
        const sendOSNotif = () => new window.Notification(title, { body, silent: false });
        if (window.Notification.permission === 'granted') {
          sendOSNotif();
        } else if (window.Notification.permission !== 'denied') {
          window.Notification.requestPermission().then(p => { if (p === 'granted') sendOSNotif(); });
        }
      }
    });
    
    return () => {
      channel.off('notification', notifRef);
    };
  }
}));